<template>
  <div class="container">
    <rewards-programs-title />
    <rewards-programs-tabs class="m-bx4" initial-tab-value="rewards" />
    <rewards-programs-rewards-toggle />
    <div class="t m-bx2">
      Offering a mix of meaningful {{ $i18n.t('shoutouts.reward', { count: 2 }) }} makes recognition more fun—and more likely to stick.
      We recommend starting with at least 3 {{ $i18n.t('shoutouts.reward', { count: 2 }) }} at different {{ $i18n.t('shoutouts.point', { count: 1 }) }} values.
      When someone redeems a {{ $i18n.t('shoutouts.reward', { count: 1 }) }}, we’ll notify the admin. Fulfillment is handled by your team for now.
    </div>
    <div class="separator" />
    <rewards-programs-fulfillment-channel />
    <div class="separator m-bx2" />
    <shoutouts-programs-gift-card-toggle
      :points-config="pointsConfig"
      :dollars-per-point="dollarsPerPoint"
      :gift-card-listing="giftCardListing"
    />
    <div class="separator m-bx2" />
    <div
      id="active-rewards"
      class="m-bx2"
    >
      <div class="t-xl bold m-b">
        Active {{ capitalize($i18n.t('shoutouts.reward', { count: 2 })) }}
      </div>
      <donut-alert
        v-if="computedProgram.state !== 'active'"
        class="m-bx2"
        theme="warning"
        :title="`${capitalize($i18n.t('shoutouts.reward', { count: 2 }))} Program Disabled`"
        :message="`These ${$i18n.t('shoutouts.reward', { count: 2 })} and the ${$i18n.t('shoutouts.rewards_store')} will not be visible to your team until you enable the program.`"
      />
      <div
        v-if="activeListings.length > 0"
        class="t m-bx2"
      >
        These {{ $i18n.t('shoutouts.reward', { count: 2 }) }} will be visible in the {{ $i18n.t('shoutouts.rewards_store') }}.
      </div>
      <div
        v-else
        class="t m-bx2 warning-yellow"
      >
        No active {{ $i18n.t('shoutouts.reward', { count: 2 }) }}. Check out the prebuilt {{ $i18n.t('shoutouts.reward', { count: 2 }) }} library below or create your own.
      </div>
      <div class="lattice">
        <shoutouts-rewards-listing
          v-for="listing in activeListings"
          :key="listing.id"
          :points-config="pointsConfig"
          :dollars-per-point="dollarsPerPoint"
          :listing="listing"
        />
      </div>
    </div>
    <div
      v-if="inactiveListings.length > 0"
      id="inactive-rewards"
      class="m-bx2"
    >
      <div class="t t-xl bold m-b">
        Inactive {{ capitalize($i18n.t('shoutouts.reward', { count: 2 })) }}
      </div>
      <div class="t m-b">
        These {{ $i18n.t('shoutouts.reward', { count: 2 }) }} are no longer visible in the {{ $i18n.t('shoutouts.rewards_store') }}. Edit visibility to turn them back on.
      </div>
      <div class="lattice">
        <shoutouts-rewards-listing
          v-for="listing in inactiveListings"
          :key="listing.id"
          :points-config="pointsConfig"
          :dollars-per-point="dollarsPerPoint"
          :listing="listing"
        />
      </div>
    </div>
    <div
      id="reward-library"
      class="m-bx2"
    >
      <div class="t t-xl bold m-b">
        Prebuilt {{ capitalize($i18n.t('shoutouts.reward', { count: 2 })) }} Library
      </div>
      <div class="t m-b">
        Start with a template, or create your own from scratch.
      </div>
      <div class="lattice">
        <shoutouts-rewards-listing
          v-for="listing in presets"
          :key="listing.name"
          :points-config="pointsConfig"
          :dollars-per-point="dollarsPerPoint"
          :listing="listing"
          :preset="true"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
  import { computed, inject } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize, compact, find, filter } from 'lodash';
  import sortListings from 'lib/rewards/sort-listings';

  const $i18n = inject('$i18n');
  const $externalResourceHost = inject('$externalResourceHost');
  const { state, commit } = useStore();

  const props = defineProps({
    pointsConfig: { type: Object, required: true },
    dollarsPerPoint: { type: Number, required: true },
  });

  const blankListing = {
    name: `Custom ${capitalize($i18n.t('shoutouts.reward', { count: 1 }))}`,
    short_description: `Craft a custom ${$i18n.t('shoutouts.reward', { count: 1 })}.`,
    is_monetary: true,
    blank: true,
  };

  const computedProgram = computed(() => state.rewardsProgram.program);

  const giftCardListing = computed(() => find(state.rewardsListings.listings, { is_gift_card: true }));

  const activeListings = computed(() => state.rewardsListings.listings
    .filter(listing => listing.id && listing.state === 'active' && !listing.is_gift_card)
    .sort(sortListings));

  const inactiveListings = computed(() => state.rewardsListings.listings
    .filter(listing => listing.id && listing.state === 'inactive' && !listing.is_gift_card)
    .sort(sortListings));

  const presets = computed(() => {
    let listings = state.rewardsListings.listingPresets;
    listings = filter(listings, { is_gift_card: false });

    return [blankListing, ...listings];
  });
</script>
